import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { posthog } from 'posthog-js';
import {
  LogOut,
  Sun,
  Moon,
  Settings,
  History,
  MessageCircleQuestion,
  Calendar,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useSidebar } from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { RootState } from '../../../reducers';
import { getSignoutUrl } from '../../../lib/routingUtils';
import TeamSelectMenu from '../sidebar/TeamSelectMenu';
import { useTheme } from '../../../theme/ThemeContext';
import { useUserTags } from '../../../hooks/useTags';
import Changelog from '../../common/Changelog';
import { LATEST_VERSION } from '../../common/Changelog/changelogData';
import { ContactSupportDialog } from '../../support';
import { preloadChatHistory } from '../../support/ai-assistant/chatPreloadSlice';
import HistoryModal from '../../history/HistoryModal';
import EventsDialog from '../../events/EventsDialog';

// Menu item type definition
type MenuItem = {
  path: string;
  icon: React.ReactElement;
  label: React.ReactNode;
  target?: '_blank';
  variant?: 'default' | 'destructive';
};

interface UserAvatarProps {
  inSidebar?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ inSidebar = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showEventsDialog, setShowEventsDialog] = useState(false);
  const { name, picture, email } = useSelector(
    (state: RootState) => state.settings?.user || {},
  );
  const subscription = useSelector(
    (state: RootState) => state.plan?.subscription,
  );
  const { isDarkMode, toggleTheme } = useTheme();
  const isMobileOrTablet = useMediaQuery('(max-width: 1024px)');
  const { getTag, setTag } = useUserTags();

  // Core Settings
  const mainSettings: MenuItem[] = [
    // {
    //   path: '/settings/display',
    //   icon: <Monitor />,
    //   label: t('userMenu.display'),
    // },
  ];

  // Team & Workspace Settings
  const workspaceSettings: MenuItem[] = [];

  // Additional Options
  const additionalOptions: MenuItem[] = [
    {
      path: getSignoutUrl(),
      icon: <LogOut />,
      label: t('userMenu.logout'),
      variant: 'destructive' as const,
    },
  ];

  const handleOpen = useCallback(() => {
    // Removed preload chat history on menu open - was causing excessive requests

    if (isMobileOrTablet) {
      setIsSheetOpen(true);
    } else {
      setIsOpen(true);
    }
  }, [isMobileOrTablet]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsSheetOpen(false);
  }, []);

  const handleSettingsClick = useCallback(
    (initialTab: string = 'user') => {
      // Use global modal state mechanism
      if (typeof window !== 'undefined') {
        (window as any).__bottomActionsModals = {
          ...(window as any).__bottomActionsModals,
          settingsModalOpen: true,
          settingsInitialTab: initialTab,
          setSettingsModalOpen: () => {
            if ((window as any).__bottomActionsModals) {
              (window as any).__bottomActionsModals.settingsModalOpen = false;
            }
          },
        };
      }
      handleClose();
    },
    [handleClose],
  );

  const handleSettingsButtonClick = useCallback(() => {
    handleSettingsClick('user');
  }, [handleSettingsClick]);

  const handleChangelogClick = useCallback(() => {
    posthog.capture('whats-new-clicked');
    setTag({ key: 'changelog-seen-version', value: LATEST_VERSION });
    setChangelogOpen(true);
    handleClose();
  }, [setTag, handleClose]);

  const handleChangelogClose = useCallback(() => {
    setChangelogOpen(false);
  }, []);

  const handleContactSupportClick = useCallback(() => {
    // Trigger chat history preload when opening support dialog
    dispatch(preloadChatHistory());
    setSupportDialogOpen(true);
    handleClose();
  }, [dispatch, handleClose]);

  const handleSupportClose = useCallback(() => {
    setSupportDialogOpen(false);
  }, []);

  const handleHistoryClick = useCallback(() => {
    setShowHistoryModal(true);
    handleClose();
  }, [handleClose]);

  const handleEventsClick = useCallback(() => {
    setShowEventsDialog(true);
    handleClose();
  }, [handleClose]);

  const handleNavigation = useCallback(
    (path: string, target?: string) => {
      if (target === '_blank') {
        window.open(path, '_blank');
      } else if (path.startsWith('/settings/')) {
        // Extract settings tab from path and open settings modal
        const settingsTab = path.split('/settings/')[1] || 'user';
        handleSettingsClick(settingsTab);
      } else {
        navigate(path);
      }
      handleClose();
    },
    [navigate, handleClose, handleSettingsClick],
  );

  // Get user initials for avatar fallback
  const getInitials = (userName: string) => {
    if (!userName) return '';
    const names = userName.split(' ').filter((n) => n.length > 0);
    if (names.length === 0) return '';
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (
      names[0].charAt(0) + names[names.length - 1].charAt(0)
    ).toUpperCase();
  };

  // Get formatted plan name
  const getFormattedPlanName = (plan?: string) => {
    if (!plan) return 'Free';

    const normalized = plan.toLowerCase();
    switch (normalized) {
      case 'free':
      case 'free_loggedin':
        return 'Free';
      case 'growth':
        return 'Growth';
      case 'business':
        return 'Business';
      case 'enterprise':
        return 'Enterprise';
      case 'basic':
        return 'Basic';
      case 'standard':
        return 'Standard';
      case 'premium':
        return 'Premium';
      case 'pro':
        return 'Pro';
      default:
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }
  };

  const renderSheetItems = (items: MenuItem[]) =>
    items.map(({ path, icon, label, target, variant }) => (
      <button
        type="button"
        key={path}
        onClick={() => handleNavigation(path, target)}
        className={cn(
          'flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent',
          variant === 'destructive' &&
            'text-destructive hover:bg-destructive/10',
        )}
      >
        {React.cloneElement(icon, { className: 'mr-3 h-4 w-4' })}
        <span>{label}</span>
      </button>
    ));

  // Menu content for dropdown
  const dropdownContent = (
    <div className="py-1 overflow-hidden">
      {/* User Profile Section */}
      <div className="px-6 py-3 overflow-hidden">
        <div className="flex items-center gap-3 overflow-hidden">
          <Avatar
            className="h-9 w-9 flex-shrink-0 cursor-pointer hover:opacity-80"
            onClick={() => handleNavigation('/settings/user')}
          >
            <AvatarImage src={picture} alt={name} />
            <AvatarFallback className="text-sm">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <button
            type="button"
            className="flex-1 min-w-0 overflow-hidden text-left cursor-pointer hover:opacity-80"
            onClick={() => handleNavigation('/settings/user')}
          >
            <p className="font-medium truncate">{name}</p>
            <p className="text-sm text-muted-foreground truncate">{email}</p>
          </button>
        </div>
      </div>

      {/* Team Selection */}
      <div className="overflow-hidden">
        <TeamSelectMenu onClose={handleClose} />
      </div>

      {/* My Meetings and History */}
      <div className="px-3 py-2 border-b space-y-1">
        <button
          type="button"
          onClick={handleEventsClick}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
        >
          <Calendar className="mr-3 h-4 w-4" />
          <span>{t('drawer.events.title', 'My Meetings')}</span>
        </button>

        <button
          type="button"
          onClick={handleHistoryClick}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
        >
          <Clock className="mr-3 h-4 w-4" />
          <span>{t('drawer.actions.history', 'History')}</span>
        </button>
      </div>

      {(mainSettings.length > 0 || workspaceSettings.length > 0) && (
        <div className="px-3 py-2 border-b space-y-1">
          {mainSettings.map(({ path, icon, label, target }) => (
            <button
              type="button"
              key={path}
              onClick={() => handleNavigation(path, target)}
              className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
            >
              {React.cloneElement(icon, { className: 'mr-3 h-4 w-4' })}
              <span>{label}</span>
            </button>
          ))}
          {workspaceSettings.map(({ path, icon, label, target }) => (
            <button
              type="button"
              key={path}
              onClick={() => handleNavigation(path, target)}
              className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
            >
              {React.cloneElement(icon, { className: 'mr-3 h-4 w-4' })}
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="px-3 py-2 space-y-1">
        <button
          type="button"
          onClick={handleSettingsButtonClick}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
          data-testid="settings-button"
        >
          <Settings className="mr-3 h-4 w-4" />
          <span>{t('userMenu.settings', 'Settings')}</span>
        </button>

        <button
          type="button"
          onClick={handleChangelogClick}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
        >
          <History className="mr-3 h-4 w-4" />
          <span className="flex-1 text-left">
            {t('userMenu.whatsNew', "What's New")}
          </span>
          {getTag('changelog-seen-version') !== LATEST_VERSION && (
            <Badge variant="destructive" className="h-2 w-2 p-0" />
          )}
        </button>

        <button
          type="button"
          onClick={handleContactSupportClick}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
        >
          <MessageCircleQuestion className="mr-3 h-4 w-4" />
          <span>{t('drawer.actions.contactSupport', 'Contact Support')}</span>
        </button>

        <button
          type="button"
          onClick={toggleTheme}
          className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
        >
          {isDarkMode ? (
            <Sun className="mr-3 h-4 w-4" />
          ) : (
            <Moon className="mr-3 h-4 w-4" />
          )}
          <span>
            {isDarkMode ? t('userMenu.lightMode') : t('userMenu.darkMode')}
          </span>
        </button>

        {/* Logout */}
        {additionalOptions.map(({ path, icon, label, target, variant }) => (
          <button
            type="button"
            key={path}
            onClick={() => handleNavigation(path, target)}
            className={cn(
              'flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent',
              variant === 'destructive' &&
                'text-destructive hover:bg-destructive/10',
            )}
          >
            {React.cloneElement(icon, { className: 'mr-3 h-4 w-4' })}
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Sheet content for mobile/tablet (identical structure to dropdown)
  const sheetContent = (
    <div className="flex flex-col h-full py-1 overflow-hidden">
      {/* User Profile Section */}
      <div className="px-6 py-3 overflow-hidden">
        <div className="flex items-center gap-3 overflow-hidden">
          <Avatar
            className="h-9 w-9 flex-shrink-0 cursor-pointer hover:opacity-80"
            onClick={() => handleNavigation('/settings/user')}
          >
            <AvatarImage src={picture} alt={name} />
            <AvatarFallback className="text-sm">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <button
            type="button"
            className="flex-1 min-w-0 overflow-hidden text-left cursor-pointer hover:opacity-80"
            onClick={() => handleNavigation('/settings/user')}
          >
            <p className="font-medium truncate">{name}</p>
            <p className="text-sm text-muted-foreground truncate">{email}</p>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Team Selection */}
        <div className="overflow-hidden">
          <TeamSelectMenu onClose={handleClose} />
        </div>

        {/* My Meetings and History */}
        <div className="px-3 py-2 border-b space-y-1">
          <button
            type="button"
            onClick={handleEventsClick}
            className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            <Calendar className="mr-3 h-4 w-4" />
            <span>{t('drawer.events.title', 'My Meetings')}</span>
          </button>

          <button
            type="button"
            onClick={handleHistoryClick}
            className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            <Clock className="mr-3 h-4 w-4" />
            <span>{t('drawer.actions.history', 'History')}</span>
          </button>
        </div>

        {(mainSettings.length > 0 || workspaceSettings.length > 0) && (
          <div className="px-3 py-2 border-b space-y-1">
            {renderSheetItems(mainSettings)}
            {renderSheetItems(workspaceSettings)}
          </div>
        )}

        {/* Actions */}
        <div className="px-3 py-2 space-y-1">
          <button
            type="button"
            onClick={handleSettingsButtonClick}
            className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
            data-testid="settings-button"
          >
            <Settings className="mr-3 h-4 w-4" />
            <span>{t('userMenu.settings', 'Settings')}</span>
          </button>

          <button
            type="button"
            onClick={handleChangelogClick}
            className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            <History className="mr-3 h-4 w-4" />
            <span className="flex-1 text-left">
              {t('userMenu.whatsNew', "What's New")}
            </span>
            {getTag('changelog-seen-version') !== LATEST_VERSION && (
              <Badge variant="destructive" className="h-2 w-2 p-0" />
            )}
          </button>

          <button
            type="button"
            onClick={handleContactSupportClick}
            className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            <MessageCircleQuestion className="mr-3 h-4 w-4" />
            <span>{t('drawer.actions.contactSupport', 'Contact Support')}</span>
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            className="flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            {isDarkMode ? (
              <Sun className="mr-3 h-4 w-4" />
            ) : (
              <Moon className="mr-3 h-4 w-4" />
            )}
            <span>
              {isDarkMode ? t('userMenu.lightMode') : t('userMenu.darkMode')}
            </span>
          </button>

          {/* Logout */}
          {renderSheetItems(additionalOptions)}
        </div>
      </div>
    </div>
  );

  // If in sidebar, render as a button like other menu items
  if (inSidebar) {
    const { open: sidebarOpen, isMobile: isSidebarMobile } = useSidebar();

    // Show text when sidebar is open OR when it's in mobile drawer mode
    const showText = sidebarOpen || isSidebarMobile;

    const button = (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'w-full rounded-md flex items-center transition-all duration-200 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200',
              'justify-start gap-3 pl-1.5 pr-3 py-2',
            )}
            data-testid="profile-avatar"
          >
            <Avatar className="h-7 w-7 flex-shrink-0">
              <AvatarImage src={picture} alt={name} />
              <AvatarFallback className="text-sm">
                {getInitials(name)}
              </AvatarFallback>
            </Avatar>
            <div
              className={cn(
                'flex-1 text-left transition-opacity duration-100 min-w-0',
                showText ? 'opacity-100' : 'w-0 opacity-0 overflow-hidden',
              )}
            >
              <div className="truncate text-sm text-gray-700 dark:text-gray-200">
                {name}
              </div>
              <div className="text-xs text-muted-foreground">
                {getFormattedPlanName(subscription?.package || 'free')}
              </div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          alignOffset={0}
          side="top"
          className="w-[calc(var(--sidebar-width)-16px)] max-w-[280px] p-0 overflow-hidden"
          sideOffset={8}
        >
          {dropdownContent}
        </DropdownMenuContent>
      </DropdownMenu>
    );

    // Wrap in tooltip when sidebar is collapsed
    const avatarButton = !sidebarOpen ? (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right">
          <div className="font-medium">{name}</div>
          <div className="text-xs text-muted-foreground">{email}</div>
        </TooltipContent>
      </Tooltip>
    ) : (
      button
    );

    return (
      <>
        {avatarButton}

        {/* Changelog Modal */}
        <Changelog open={changelogOpen} onClose={handleChangelogClose} />

        {/* Support Dialog */}
        {supportDialogOpen && (
          <ContactSupportDialog onClose={handleSupportClose} />
        )}

        {/* History Modal */}
        {showHistoryModal && (
          <HistoryModal
            open={showHistoryModal}
            onClose={() => setShowHistoryModal(false)}
          />
        )}

        {/* Events Dialog */}
        {showEventsDialog && (
          <EventsDialog
            open={showEventsDialog}
            onClose={() => setShowEventsDialog(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      {/* Desktop Dropdown */}
      {!isMobileOrTablet && (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-full flex items-center justify-center h-10"
              data-testid="profile-avatar"
            >
              <Avatar className="h-10 w-10 cursor-pointer transition-opacity hover:opacity-80">
                <AvatarImage src={picture} alt={name} />
                <AvatarFallback>{getInitials(name)}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[280px] max-w-[280px] p-0 overflow-hidden"
            sideOffset={5}
          >
            {dropdownContent}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Mobile/Tablet Sheet */}
      {isMobileOrTablet && (
        <>
          <button
            type="button"
            onClick={handleOpen}
            className="outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-full flex items-center justify-center h-10"
            data-testid="profile-avatar"
          >
            <Avatar className="h-10 w-10 cursor-pointer transition-opacity hover:opacity-80">
              <AvatarImage src={picture} alt={name} />
              <AvatarFallback>{getInitials(name)}</AvatarFallback>
            </Avatar>
          </button>

          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetContent
              side="right"
              className="w-[280px] sm:w-[320px] max-w-[280px] sm:max-w-[320px] p-0 overflow-hidden"
            >
              <SheetHeader className="sr-only">
                <h2>{t('userMenu.accountSettings')}</h2>
              </SheetHeader>
              {sheetContent}
            </SheetContent>
          </Sheet>
        </>
      )}

      {/* Changelog Modal */}
      <Changelog open={changelogOpen} onClose={handleChangelogClose} />

      {/* Support Dialog */}
      {supportDialogOpen && (
        <ContactSupportDialog onClose={handleSupportClose} />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <HistoryModal
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      {/* Events Dialog */}
      {showEventsDialog && (
        <EventsDialog
          open={showEventsDialog}
          onClose={() => setShowEventsDialog(false)}
        />
      )}
    </>
  );
};

export default UserAvatar;
